import { Box, Typography } from "@mui/material";
import { ArrowDownwardIcon, ArrowRightIcon } from "assets/styles/icons";
import { useProgress } from "context/ProgressProvider";
import React, { ReactElement, useEffect } from "react";
import { useSelector } from "react-redux";
import Utils from "shared/helpers/Utils";
import dayjs from "dayjs";

const Accordion: React.FC<{
  expanded: boolean;
  onChange: (event: React.MouseEvent, isExpanded: boolean) => void;
  children: React.ReactNode;
}> = ({ expanded, onChange, children }) => {
  return (
    <Box
      sx={{
        "&:last-child": {
          borderBottom: "none",
        },
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            expanded,
            onChange,
          });
        }
        return child;
      })}
    </Box>
  );
};

const AccordionSummary: React.FC<{
  children: React.ReactNode;
  expandIcon?: React.ReactNode;
  "aria-controls"?: string;
  id?: string;
  expanded?: boolean;
  onChange?: (event: React.MouseEvent, isExpanded: boolean) => void;
}> = ({ children, expandIcon, expanded = false, onChange, ...props }) => {
  return (
    <Box
      style={{
        padding: "0 6px",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "white",
        transition: "background-color 0.2s ease",
        border: "none",
        outline: "none",
      }}
      onClick={(e) => onChange && onChange(e, !expanded)}
      {...props}
    >
      <Box sx={{ fontSize: "16px", fontWeight: 500, color: "#333" }}>
        {children}
      </Box>
      <Box sx={{ color: "#666", transition: "transform 0.2s ease" }}>
        {expandIcon}
      </Box>
    </Box>
  );
};

const AccordionDetails: React.FC<{
  children: React.ReactNode;
  expanded?: boolean;
}> = ({ children, expanded = false }) => {
  return (
    <Box
      style={{
        maxHeight: expanded ? "4000px" : "0",
        overflow: "hidden",
        transition: "max-height 0.3s ease",
        background: "white",
      }}
    >
      <Box sx={{ padding: "12px 6px 0 6px" }}>{children}</Box>
    </Box>
  );
};

const ExpandMoreIcon: React.FC<{ expanded?: boolean }> = ({
  expanded = false,
}) => {
  return <ArrowDownwardIcon expanded={expanded} />;
};

const DetailRow: React.FC<{
  label: string;
  value: string | ReactElement | number;
  showArrow?: boolean;
  onClick?: any;
  blockEdit?: boolean;
}> = ({ label, value, showArrow = true, onClick, blockEdit = false }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px",
        cursor: blockEdit ? "not-allowed" : "pointer",
        transition: "background-color 0.2s ease",
        border: "1px solid #E5E5E5",
        borderRadius: "12px",
        marginTop: "12px",
        "&:first-child": {
          marginTop: "0",
        },
      }}
      onClick={onClick}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#474747", marginBottom: "4px" }}
        >
          {label}
        </Typography>
        <Typography
          sx={{ fontSize: "16px", color: "#0A0A0A", marginBottom: "4px" }}
        >
          {value}
        </Typography>
      </Box>
      {showArrow && (
        <Box sx={{ color: "#0A0A0A", marginLeft: "16px" }}>
          <ArrowRightIcon />
        </Box>
      )}
    </Box>
  );
};

const AccordionReviewComponents = ({
  expanded,
  handleChange,
  setShowDialog,
  setExistingLoanStatus,
}) => {
  const formatLoanText = (text) => {
    return text
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const { currentProfile, isBroker } = useProgress();

  return (
    <>
      <Box
        sx={{
          padding: "12px",
          border: "1px solid #E5E5E5",
          borderRadius: "16px",
        }}
      >
        <Accordion
          expanded={expanded === "panel1"}
          onChange={handleChange("panel1")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon expanded={expanded === "panel1"} />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography variant="h3">
              {isBroker ? "Customer details" : "Your details"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DetailRow
              label="Legal name"
              value={`${currentProfile.firstName} ${currentProfile.lastName}`}
              onClick={() => setShowDialog("details-1")}
            />
            <DetailRow
              label="Email address"
              value={currentProfile.email}
              // onClick={() => setShowDialog("details-2")}
              blockEdit
            />
            <DetailRow
              label="Phone number"
              value={currentProfile.phoneNumber}
              onClick={() => setShowDialog("details-3")}
            />
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Your loan */}
      <Box
        sx={{
          padding: "12px",
          border: "1px solid #E5E5E5",
          borderRadius: "16px",
        }}
        mt={4}
      >
        <Accordion
          expanded={expanded === "panel2"}
          onChange={handleChange("panel2")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon expanded={expanded === "panel2"} />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography variant="h3">
              {isBroker ? "Customer loan" : "Your loan"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DetailRow
              label="Loan amount"
              value={`$${Utils.formatCurrency(currentProfile.loanAmount)} AUD`}
              onClick={() => setShowDialog("loan-1")}
            />
            <DetailRow
              label="Business’s annual turnover"
              value={`$${Utils.formatCurrency(
                currentProfile.annualTurnover,
              )} AUD`}
              onClick={() => setShowDialog("loan-2")}
            />
            <DetailRow
              label="Has ATO claims?"
              value={currentProfile.hasATOClaims ? "Yes" : "No"}
              onClick={() => setShowDialog("loan-3")}
            />
            <DetailRow
              label="Has tax incentive?"
              value={currentProfile.hasTaxIncentive ? "Yes" : "No"}
              onClick={() => setShowDialog("loan-4")}
            />
            <DetailRow
              label="Tax year"
              value={currentProfile.taxYear}
              onClick={() => setShowDialog("loan-5")}
            />
            <DetailRow
              label="Tax filing date"
              value={dayjs(currentProfile.estimatedTaxFilingDate).format(
                "YYYY-MM-DD",
              )}
              onClick={() => {
                setShowDialog("loan-6");
              }}
            />
            <DetailRow
              label="Estimated expenditure amount"
              value={`$${Utils.formatCurrency(
                currentProfile.estimatedExpenditureAmount,
              )} AUD`}
              onClick={() => setShowDialog("loan-7")}
            />
            {isBroker && (
              <DetailRow
                label="Broker Commission Rate"
                value={`${currentProfile.brokerCommissionRate}%` || "0"}
                onClick={() => setShowDialog("loan-8")}
              />
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Your Business */}
      {/* <Box
        sx={{
          padding: "12px",
          border: "1px solid #E5E5E5",
          borderRadius: "16px",
        }}
        mt={4}
      >
        <Accordion
          expanded={expanded === "panel3"}
          onChange={handleChange("panel3")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon expanded={expanded === "panel3"} />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography variant="h3">Your business</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DetailRow
              label="Entity type"
              value={
                (currentProfile.entityType &&
                  formatLoanText(currentProfile.entityType)) ||
                "-"
              }
              onClick={() => setShowDialog("business-1")}
            />
            <DetailRow
              label="Company address"
              value={currentProfile.companyAddress}
              onClick={() => setShowDialog("business-2")}
            />
            <DetailRow
              label="ABN"
              value={currentProfile.abn}
              onClick={() => setShowDialog("business-3")}
            />
            <DetailRow
              label="Business or Company address"
              value={currentProfile.businnessOrCompanyAddress}
              onClick={() => setShowDialog("business-4")}
            />
            <DetailRow
              label="AUSIndustry Registration Date"
              value={currentProfile.auRegDate}
              onClick={() => setShowDialog("business-5")}
            />
          </AccordionDetails>
        </Accordion>
      </Box> */}
    </>
  );
};

export default AccordionReviewComponents;
